import { alpha, Stack, Table, TableBody, TableCell, TableContainer, TableRow, tableRowClasses, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import {
  enqueueSnackbar,
  formatAmount,
  getFormattedDateString,
  ITableHeadCustomType,
  TableHeadCustom,
  TablePaginationCustom,
  TableSpinner,
  useTable,
  useUserDetailsStore,
} from '@zeta-core';
import { FinancialMisService, getErrorMessage } from '@zeta-services';
import { IEmptyRequestResponse, IGenericErrorResponse } from '@zeta-types';

const HEAD_LABEL: ITableHeadCustomType[] = [
  { label: 'Distribution Date', width: 353, show: true, id: 'distributionDate' },
  { label: 'Ex-Date', width: 353, show: true, id: 'exDate' },
  { label: 'Share Class', width: 353, show: true, id: 'securityName' },
  { label: 'ISIN', width: 353, show: true, id: 'isin' },
  { label: 'Dividend', width: 353, show: true, id: 'price' },
  { label: 'Paid Date', width: 353, show: true, id: 'paymentDate' },
];

const DistributionsTable = ({ isClient }: { isClient?: boolean }) => {
  const userStore = useUserDetailsStore((state) => state.user);
  const location = useLocation();
  const state = location?.state;

  const clientID = isClient ? userStore?.accessibleEntityID : state?.details?.clientId;
  const theme = useTheme();
  const table = useTable({ defaultRowsPerPage: 5, defaultOrderBy: 'distributionDate' });

  const [distributionsData, setDistributionsData] = useState();

  const getDistributionsData = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getPortfolioDistributions(payload) : FinancialMisService().getPortfolioDistributionsAdvisor(payload),
    onSuccess: (response) => {
      setDistributionsData(response?.data);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    getDistributionsData?.mutateAsync({
      clientID,
      accountId: state?.details?.accountId || state?.accountId,
      pageSize: table?.rowsPerPage,
      pageIndex: table?.page + 1,
      sort: table.orderBy,
      desc: table.order === 'desc',
    });
  }, [table?.rowsPerPage, table?.page, clientID, table.order, table.orderBy]);

  return (
    <>
      <TableContainer>
        <Table
          role="grid"
          stickyHeader
          size={table.dense ? 'small' : 'medium'}
          sx={{
            [`& .${tableRowClasses.root}`]: {
              '&:nth-of-type(even)': {
                backgroundColor: alpha(theme.palette.grey[500], 0.08),
              },
              '&:last-child td, &:last-child th': {
                border: 0,
              },
            },
          }}
        >
          <TableHeadCustom headLabel={HEAD_LABEL} order={table.order} orderBy={table.orderBy} onSort={table.onSort} />

          <TableBody>
            {getDistributionsData?.isPending && <TableSpinner colSpan={12} />}
            {!distributionsData?.distributions?.length && !getDistributionsData?.isPending && (
              <TableCell colSpan={12} sx={{ borderLeft: '3px solid #000000 !important' }}>
                <Stack alignItems="center" justifyContent="center" height="15vh">
                  <Typography variant="subtitle2" color="text.secondary">
                    No Data available
                  </Typography>
                </Stack>
              </TableCell>
            )}
            {!getDistributionsData?.isPending &&
              distributionsData?.distributions?.map((item) => {
                return (
                  <TableRow key={item.distributionId}>
                    <TableRow key={item.distributionId}>
                      <TableCell sx={{ borderLeft: '3px solid #000000 !important' }}>
                        {getFormattedDateString(item?.distributionDate, 'DD/MM/YYYY') || '--'}
                      </TableCell>
                      <TableCell>{getFormattedDateString(item?.exDate, 'DD/MM/YYYY') || '--'}</TableCell>
                      <TableCell>{item?.securityName || '--'}</TableCell>
                      <TableCell>{item?.isin || '--'}</TableCell>
                      <TableCell>{formatAmount(item?.price, item.currency)}</TableCell>
                      <TableCell>{getFormattedDateString(item?.paymentDate, 'DD/MM/YYYY') || '--'}</TableCell>
                    </TableRow>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationCustom
        page={table.page}
        rowsPerPage={table.rowsPerPage}
        onPageChange={table.onChangePage}
        onChangeDense={table.onChangeDense}
        count={getDistributionsData?.data?.data?.totalRecords || 0}
        onRowsPerPageChange={table.onChangeRowsPerPage}
      />
    </>
  );
};

export default DistributionsTable;
